import { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatAxiosError } from "@/lib/api";
import { absUrl } from "@/lib/config";
import {
  CheckCircle, Circle, UploadSimple, DownloadSimple, Trash, FloppyDisk,
  Sparkle, Warning, Rocket, X,
} from "@phosphor-icons/react";

// ============ DYNAMIC FIELD RENDERER ============
// Renders any onboarding item type. Value shape:
//   text/textarea/phone/email/select/date: string
//   number: number|string
//   acknowledgment: boolean
//   file: file_id string
//   multi_file: [file_id, ...]
//   address: {street, apartment?, city, state, zip, country}

export function OnboardingField({ item, value, onChange, filesMeta = {}, readOnly = false }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const upload = async (file) => {
    if (!file) return null;
    setUploading(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return { id: r.data.id, filename: file.name, length: file.size, content_type: file.type };
    } catch (e) {
      setErr(formatAxiosError(e));
      return null;
    } finally { setUploading(false); }
  };

  if (item.type === "text" || item.type === "phone" || item.type === "email") {
    return (
      <input
        type={item.type === "email" ? "email" : item.type === "phone" ? "tel" : "text"}
        value={value ?? ""}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-border px-3 py-2 text-sm disabled:bg-muted"
        data-testid={`ob-field-${item.key}`}
      />
    );
  }

  if (item.type === "number") {
    return (
      <input
        type="number"
        value={value ?? ""}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-border px-3 py-2 text-sm"
        data-testid={`ob-field-${item.key}`}
      />
    );
  }

  if (item.type === "textarea") {
    return (
      <textarea
        rows={4}
        value={value ?? ""}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-border px-3 py-2 text-sm"
        data-testid={`ob-field-${item.key}`}
      />
    );
  }

  if (item.type === "date") {
    return (
      <input
        type="date"
        value={value ?? ""}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-border px-3 py-2 text-sm"
        data-testid={`ob-field-${item.key}`}
      />
    );
  }

  if (item.type === "select") {
    return (
      <select
        value={value ?? ""}
        disabled={readOnly}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-border px-3 py-2 text-sm"
        data-testid={`ob-field-${item.key}`}
      >
        <option value="">— choose —</option>
        {(item.options || []).map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }

  if (item.type === "acknowledgment") {
    return (
      <label className="flex items-start gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={!!value}
          disabled={readOnly}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 accent-[#002FA7]"
          data-testid={`ob-field-${item.key}`}
        />
        <span>{item.label}</span>
      </label>
    );
  }

  if (item.type === "file") {
    const meta = value ? (filesMeta[value] || { id: value, filename: "file" }) : null;
    return (
      <div>
        {meta ? (
          <div className="border border-border p-3 flex items-center justify-between gap-3">
            <div className="text-sm truncate">
              <span className="font-semibold">{meta.filename}</span>
              {meta.length > 0 && <span className="text-muted-foreground ml-2 text-[11px]">({(meta.length / 1024).toFixed(1)} KB)</span>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a
                href={absUrl(`/api/files/${meta.id}`)}
                target="_blank"
                rel="noreferrer"
                className="border border-border px-2 py-1 text-xs hover:bg-muted flex items-center gap-1"
              >
                <DownloadSimple size={12} weight="bold" /> View
              </a>
              {!readOnly && (
                <button onClick={() => onChange(null)} className="border border-border p-1 hover:bg-muted" title="Remove">
                  <Trash size={12} weight="bold" />
                </button>
              )}
            </div>
          </div>
        ) : (
          !readOnly && (
            <label className="border border-dashed border-border p-4 flex items-center justify-center gap-2 text-sm cursor-pointer hover:bg-muted">
              <UploadSimple size={14} weight="bold" />
              {uploading ? "Uploading…" : "Upload file"}
              <input
                type="file"
                className="hidden"
                onChange={async (e) => {
                  const f = await upload(e.target.files?.[0]);
                  if (f) onChange(f.id);
                }}
                data-testid={`ob-upload-${item.key}`}
              />
            </label>
          )
        )}
        {err && <div className="text-xs text-[#FF3B30] mt-1">{err}</div>}
      </div>
    );
  }

  if (item.type === "multi_file") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-2">
        {arr.map(fid => {
          const meta = filesMeta[fid] || { id: fid, filename: "file" };
          return (
            <div key={fid} className="border border-border p-2 flex items-center justify-between text-sm">
              <span className="truncate">{meta.filename}</span>
              <div className="flex items-center gap-2 shrink-0">
                <a href={absUrl(`/api/files/${fid}`)} target="_blank" rel="noreferrer" className="border border-border px-2 py-0.5 text-xs hover:bg-muted flex items-center gap-1">
                  <DownloadSimple size={11} weight="bold" /> View
                </a>
                {!readOnly && (
                  <button onClick={() => onChange(arr.filter(x => x !== fid))} className="border border-border p-1 hover:bg-muted">
                    <Trash size={11} weight="bold" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {!readOnly && (
          <label className="border border-dashed border-border p-3 flex items-center justify-center gap-2 text-sm cursor-pointer hover:bg-muted">
            <UploadSimple size={14} weight="bold" />
            {uploading ? "Uploading…" : arr.length ? "Add another file" : "Upload file(s)"}
            <input
              type="file"
              className="hidden"
              onChange={async (e) => {
                const f = await upload(e.target.files?.[0]);
                if (f) onChange([...arr, f.id]);
              }}
              data-testid={`ob-multi-upload-${item.key}`}
            />
          </label>
        )}
        {err && <div className="text-xs text-[#FF3B30] mt-1">{err}</div>}
      </div>
    );
  }

  if (item.type === "address") {
    const v = value && typeof value === "object" ? value : {};
    const set = (k, val) => onChange({ ...v, [k]: val });
    return (
      <div className="grid grid-cols-2 gap-2">
        <input type="text" placeholder="Street" value={v.street || ""} readOnly={readOnly} onChange={e => set("street", e.target.value)} className="col-span-2 border border-border px-3 py-2 text-sm" data-testid={`ob-addr-${item.key}-street`} />
        <input type="text" placeholder="Apartment / suite (optional)" value={v.apartment || ""} readOnly={readOnly} onChange={e => set("apartment", e.target.value)} className="col-span-2 border border-border px-3 py-2 text-sm" />
        <input type="text" placeholder="City" value={v.city || ""} readOnly={readOnly} onChange={e => set("city", e.target.value)} className="border border-border px-3 py-2 text-sm" data-testid={`ob-addr-${item.key}-city`} />
        <input type="text" placeholder="State / Province" value={v.state || ""} readOnly={readOnly} onChange={e => set("state", e.target.value)} className="border border-border px-3 py-2 text-sm" />
        <input type="text" placeholder="ZIP / Postal" value={v.zip || ""} readOnly={readOnly} onChange={e => set("zip", e.target.value)} className="border border-border px-3 py-2 text-sm" />
        <input type="text" placeholder="Country" value={v.country || ""} readOnly={readOnly} onChange={e => set("country", e.target.value)} className="border border-border px-3 py-2 text-sm" data-testid={`ob-addr-${item.key}-country`} />
      </div>
    );
  }

  return <div className="text-xs text-muted-foreground">Unsupported field type: {item.type}</div>;
}

function isItemComplete(item, value) {
  if (value === undefined || value === null) return false;
  const t = item.type;
  if (["text", "textarea", "phone", "email", "date", "select"].includes(t)) return String(value).trim().length > 0;
  if (t === "number") return value !== "" && !isNaN(Number(value));
  if (t === "acknowledgment") return value === true || value === "true";
  if (t === "file") return typeof value === "string" && value.length > 0;
  if (t === "multi_file") return Array.isArray(value) && value.length > 0;
  if (t === "address") return value && typeof value === "object" && ["street", "city", "country"].every(k => (value[k] || "").toString().trim());
  return !!value;
}

// ============ ONBOARDING PAGE ============
export default function Onboarding({ modal = false, onClose }) {
  const [cfg, setCfg] = useState(null);
  const [sub, setSub] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [activeSection, setActiveSection] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([api.get("/onboarding/config"), api.get("/onboarding/me")]);
      setCfg(c.data);
      setSub(s.data);
      setValues(s.data.values || {});
      if (!activeSection && c.data.sections.length) setActiveSection(c.data.sections[0].id);
    } catch (e) {
      setMsg({ type: "error", text: formatAxiosError(e) });
    } finally { setLoading(false); }
  }, [activeSection]);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/onboarding/me", { values });
      setSub(r.data);
      flash("ok", "Progress saved.");
    } catch (e) { flash("error", formatAxiosError(e)); }
    finally { setSaving(false); }
  };

  const submitComplete = async () => {
    setCompleting(true);
    try {
      // Save first
      await api.put("/onboarding/me", { values });
      const r = await api.post("/onboarding/me/complete");
      setSub(r.data);
      flash("ok", "Onboarding complete! Welcome aboard.");
    } catch (e) { flash("error", formatAxiosError(e)); }
    finally { setCompleting(false); }
  };

  const sectionsSorted = useMemo(
    () => (cfg?.sections || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [cfg]
  );

  const itemsByS = useMemo(() => {
    const map = {};
    (cfg?.items || []).forEach(it => {
      if (!map[it.section_id]) map[it.section_id] = [];
      map[it.section_id].push(it);
    });
    Object.values(map).forEach(arr => arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    return map;
  }, [cfg]);

  const sectionProgress = (sid) => {
    const items = itemsByS[sid] || [];
    const total = items.length;
    const done = items.filter(it => isItemComplete(it, values[it.key])).length;
    const req = items.filter(it => it.required).length;
    const doneReq = items.filter(it => it.required && isItemComplete(it, values[it.key])).length;
    return { total, done, req, doneReq };
  };

  if (loading) return <div className="p-12 text-sm text-muted-foreground">Loading onboarding…</div>;
  if (!cfg || !sub) return <div className="p-12 text-sm text-[#FF3B30]">{msg.text || "Failed to load."}</div>;

  const isComplete = sub.status === "complete";
  const pct = sub.progress?.percent ?? 0;
  const reqPct = sub.progress?.required_percent ?? 0;
  const totalRequired = sub.progress?.total_required ?? 0;
  const doneRequired = sub.progress?.completed_required ?? 0;

  const wrap = modal
    ? "bg-white w-full max-w-5xl max-h-[92vh] overflow-y-auto"
    : "";

  return (
    <div className={wrap}>
      {modal && (
        <div className="p-6 border-b border-border flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="eyebrow flex items-center gap-2 mb-1">
              <Rocket size={12} weight="bold" /> Welcome to the team
            </div>
            <h2 className="font-display text-2xl font-black tracking-tight">Complete your onboarding</h2>
          </div>
          <button onClick={onClose} className="border border-border p-2 hover:bg-muted"><X size={14} weight="bold" /></button>
        </div>
      )}

      {!modal && (
        <div className="border-b border-border px-8 lg:px-12 py-8">
          <div className="eyebrow flex items-center gap-2 mb-2">
            <Sparkle size={12} weight="bold" /> Getting started
          </div>
          <h1 className="font-display text-4xl font-black tracking-tight">Your onboarding</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Please fill in the details below so we can complete your setup. Your data is only visible to
            HR and your manager. You can save progress and come back later — nothing is submitted until
            you click &quot;Complete onboarding&quot;.
          </p>
        </div>
      )}

      <div className={modal ? "p-6" : "p-8 lg:p-12"}>
        {/* Progress bar */}
        <div className="mb-8" data-testid="ob-progress">
          <div className="flex items-center justify-between mb-2 text-sm">
            <span className="font-semibold">
              {isComplete ? (
                <span className="text-[#22C55E] flex items-center gap-1"><CheckCircle size={14} weight="fill" /> Onboarding complete</span>
              ) : `${doneRequired} / ${totalRequired} required items complete`}
            </span>
            <span className="font-mono-mini text-[10px] text-muted-foreground">{pct}% overall</span>
          </div>
          <div className="h-2 bg-muted overflow-hidden">
            <div className="h-full bg-[#002FA7] transition-all" style={{ width: `${reqPct}%` }} />
          </div>
        </div>

        {msg.text && (
          <div className={`mb-4 px-4 py-2 text-sm border flex items-center gap-2 ${msg.type === "ok" ? "border-[#22C55E] bg-green-50 text-[#166534]" : "border-[#FF3B30] bg-red-50 text-[#FF3B30]"}`}>
            {msg.type === "ok" ? <CheckCircle size={14} weight="fill" /> : <Warning size={14} weight="fill" />}
            {msg.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Section nav */}
          <aside className="lg:col-span-1">
            <div className="border border-border" data-testid="ob-sections-nav">
              {sectionsSorted.map(s => {
                const p = sectionProgress(s.id);
                const done = p.req > 0 ? p.doneReq === p.req : p.done === p.total;
                const active = activeSection === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setActiveSection(s.id)}
                    className={`w-full text-left px-4 py-3 border-b border-border last:border-b-0 hover:bg-muted transition-colors ${active ? "bg-[#002FA7]/5 border-l-4 border-l-[#002FA7]" : "border-l-4 border-l-transparent"}`}
                    data-testid={`ob-section-btn-${s.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-sm font-semibold">{s.title}</span>
                      {done
                        ? <CheckCircle size={14} weight="fill" className="text-[#22C55E] shrink-0" />
                        : <Circle size={14} className="text-muted-foreground shrink-0" />}
                    </div>
                    <div className="text-[10px] font-mono-mini text-muted-foreground">
                      {p.doneReq}/{p.req} required • {p.done}/{p.total} total
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Fields */}
          <section className="lg:col-span-3">
            {sectionsSorted.filter(s => s.id === activeSection).map(s => (
              <div key={s.id} className="border border-border p-6">
                <div className="mb-5">
                  <h2 className="font-display text-2xl font-black tracking-tight">{s.title}</h2>
                  {s.description && <p className="text-sm text-muted-foreground mt-1">{s.description}</p>}
                </div>
                <div className="space-y-5">
                  {(itemsByS[s.id] || []).map(it => (
                    <div key={it.key} data-testid={`ob-item-${it.key}`}>
                      {it.type !== "acknowledgment" && (
                        <label className="eyebrow block mb-1">
                          {it.label}
                          {it.required && <span className="text-[#FF3B30] ml-1">*</span>}
                        </label>
                      )}
                      <OnboardingField
                        item={it}
                        value={values[it.key]}
                        onChange={(v) => setValues(prev => ({ ...prev, [it.key]: v }))}
                        filesMeta={sub.files_meta || {}}
                        readOnly={isComplete}
                      />
                      {it.description && (
                        <div className="text-[11px] text-muted-foreground mt-1">{it.description}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Actions */}
            {!isComplete && (
              <div className="flex items-center justify-end gap-2 mt-6">
                <button
                  onClick={save}
                  disabled={saving}
                  className="border border-border px-4 py-2 text-sm hover:bg-muted disabled:opacity-40 flex items-center gap-2"
                  data-testid="ob-save"
                >
                  <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save progress"}
                </button>
                <button
                  onClick={submitComplete}
                  disabled={completing || doneRequired < totalRequired}
                  className="btn-primary flex items-center gap-2 disabled:opacity-40"
                  data-testid="ob-complete"
                  title={doneRequired < totalRequired ? `${totalRequired - doneRequired} required item(s) remaining` : ""}
                >
                  <Rocket size={14} weight="bold" /> {completing ? "Submitting…" : "Complete onboarding"}
                </button>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
